import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CaretRight, X } from '@phosphor-icons/react';
import { DOCK } from '../../lib/navigation';
import { hasViewAccess } from '../../lib/accessControl';
import { useAuth } from '../../contexts/AuthContext';

// ── Per-item accent colours ────────────────────────────────────────────────
const ACCENT = {
  sampling:   { from: '#F43F5E', to: '#FB7185', shadow: 'rgba(244,63,94,0.55)'   },
  costing:    { from: '#F59E0B', to: '#FCD34D', shadow: 'rgba(245,158,11,0.55)'  },
  materials:  { from: '#10B981', to: '#34D399', shadow: 'rgba(16,185,129,0.55)'  },
  po:         { from: '#3B82F6', to: '#60A5FA', shadow: 'rgba(59,130,246,0.55)'  },
  cutting:    { from: '#8B5CF6', to: '#A78BFA', shadow: 'rgba(139,92,246,0.55)'  },
  production: { from: '#F97316', to: '#FB923C', shadow: 'rgba(249,115,22,0.55)'  },
  packing:    { from: '#06B6D4', to: '#22D3EE', shadow: 'rgba(6,182,212,0.55)'   },
  finance:    { from: '#6366F1', to: '#818CF8', shadow: 'rgba(99,102,241,0.55)'  },
};

const SLOT = 72; // conveyor slot width (px)

export default function BottomDock() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { user }  = useAuth();

  // ── Desktop action-sheet state (original behaviour) ────────────────────
  const [activeKey, setActiveKey] = useState(null);

  // ── Mobile conveyor state ───────────────────────────────────────────────
  const trackRef    = useRef(null);
  const scrollTimer = useRef(null);
  const blockBubble = useRef(true);  // suppresses auto-open on page-load / route-change
  const [centeredIdx, setCenteredIdx] = useState(0);
  const [bubbleOpen, setBubbleOpen]   = useState(false);
  const [hidden,     setHidden]       = useState(false);

  // Filtered items (same for both layouts)
  const items = DOCK.map(d => ({
    ...d,
    sheet: (d.sheet || []).filter(s => hasViewAccess(user, s.path)),
  })).filter(d => d.sheet.length > 0);

  // ── Circular conveyor helpers ────────────────────────────────────────────
  const N         = items.length;
  const loopItems = [...items, ...items, ...items]; // 3× for 360° infinite scroll

  // ── isActive helper ─────────────────────────────────────────────────────
  const isActive = useCallback((d) => {
    if (!d) return false;
    if (d.primaryPath && location.pathname === d.primaryPath) return true;
    return d.sheet?.some(s =>
      location.pathname === s.path ||
      (s.path !== '/dashboard' && location.pathname.startsWith(s.path.split('?')[0]))
    );
  }, [location.pathname]);

  // ── Sync mobile conveyor to active route ────────────────────────────────
  useEffect(() => {
    const idx = items.findIndex(d => isActive(d));
    const target = idx >= 0 ? idx : 0;
    // Block bubble auto-open during programmatic scroll; unblock after animation
    blockBubble.current = true;
    setBubbleOpen(false);
    requestAnimationFrame(() => {
      const el = trackRef.current;
      if (!el) return;
      // Always scroll to MIDDLE copy (N + target) so user can scroll both left and right
      const extTarget = N + target;
      const dest = Math.max(0, extTarget * SLOT + SLOT / 2 - el.offsetWidth / 2);
      el.scrollTo({ left: dest, behavior: 'smooth' });
      setCenteredIdx(extTarget);
      // Unblock after smooth-scroll animation completes (~600 ms)
      setTimeout(() => { blockBubble.current = false; }, 700);
    });
  }, [location.pathname]); // eslint-disable-line

  // ── Hide on page scroll-down ────────────────────────────────────────────
  useEffect(() => {
    const isMobile = () => window.matchMedia('(max-width: 1023px)').matches;
    let lastY = window.scrollY;
    let raf = 0;
    const onScroll = (e) => {
      if (!isMobile()) { setHidden(false); return; }
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const pageY = window.scrollY;
        const t = e?.target;
        const innerY = (t && t !== document && typeof t.scrollTop === 'number') ? t.scrollTop : 0;
        const y = pageY > 0 ? pageY : innerY;
        if (Math.abs(y - lastY) < 12) return;
        if (y > lastY && y > 80) setHidden(true);
        else if (y < lastY) setHidden(false);
        lastY = y;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true, capture: true });
    return () => { window.removeEventListener('scroll', onScroll, { capture: true }); cancelAnimationFrame(raf); };
  }, []);

  // ── Hide on keyboard ────────────────────────────────────────────────────
  useEffect(() => {
    const isMobile = () => window.matchMedia('(max-width: 1023px)').matches;
    const onFocus = (e) => { if (isMobile() && ['INPUT','TEXTAREA','SELECT'].includes(e.target.tagName)) setHidden(true); };
    const onBlur  = () => setHidden(false);
    document.addEventListener('focusin',  onFocus);
    document.addEventListener('focusout', onBlur);
    return () => { document.removeEventListener('focusin', onFocus); document.removeEventListener('focusout', onBlur); };
  }, []);

  // ── Desktop: tap to open/close action sheet ─────────────────────────────
  const onDesktopTap = (d) => {
    if (d.sheet?.length === 1) { navigate(d.sheet[0].path); setActiveKey(null); return; }
    setActiveKey(prev => prev === d.key ? null : d.key);
  };

  // ── Mobile conveyor scroll ──────────────────────────────────────────────
  const onConveyorScroll = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    let extIdx = Math.round((el.scrollLeft + el.offsetWidth / 2 - SLOT / 2) / SLOT);
    // Silent wrap: when user scrolls into a clone zone, jump to the middle copy
    if (extIdx < N) {
      el.scrollLeft += N * SLOT;
      extIdx += N;
    } else if (extIdx >= N * 2) {
      el.scrollLeft -= N * SLOT;
      extIdx -= N;
    }
    setCenteredIdx(extIdx);
    setBubbleOpen(false);
    clearTimeout(scrollTimer.current);
    // Only open bubble after a user-initiated scroll (not on programmatic page-load scroll)
    if (!blockBubble.current) {
      scrollTimer.current = setTimeout(() => setBubbleOpen(true), 280);
    }
  }, [N]);

  if (items.length === 0) return null;

  // ── Derived state for bubble ─────────────────────────────────────────────
  const centeredRealIdx = ((centeredIdx % N) + N) % N;  // always 0–N-1
  const centered  = items[centeredRealIdx];
  const accentCfg = ACCENT[centered?.key] || ACCENT.po;
  const showBubble = bubbleOpen && centered?.sheet?.length > 0;

  // Non-centered items render at 1.0× (normal button size); center pops to 1.4×
  const getScale   = (extI) => extI === centeredIdx ? 1.4 : 1.0;
  const getOpacity = (extI) => extI === centeredIdx ? 1   : 0.72;

  const navTo = (path) => { setBubbleOpen(false); setActiveKey(null); navigate(path); };

  // ── Desktop action-sheet active item ────────────────────────────────────
  const desktopActive = items.find(d => d.key === activeKey);

  // ═══════════════════════════════════════════════════════════════════════
  return (
    <>
      {/* ── DESKTOP ACTION SHEET (original upward slide, lg+ only) ──────── */}
      <div
        data-testid="dock-sheet-backdrop"
        onClick={() => setActiveKey(null)}
        className={`fixed inset-0 z-40 bg-black/40 backdrop-blur-sm transition-opacity duration-200 hidden lg:block
          ${activeKey ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
      />
      <div
        data-testid="dock-sheet"
        className={`fixed bottom-0 left-0 right-0 z-40 hidden lg:flex justify-center transform transition-transform duration-300 ease-out
          ${activeKey ? 'translate-y-0' : 'translate-y-full'}`}
      >
        <div className="mx-3 mb-[calc(88px+env(safe-area-inset-bottom,0px))] w-full max-w-xl rounded-2xl bg-white dark:bg-stone-900 shadow-2xl ring-1 ring-stone-200 dark:ring-stone-800 overflow-hidden">
          <div className="px-4 py-3 border-b border-stone-100 dark:border-stone-800 flex items-center justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-stone-400">Choose action</p>
              <p className="text-sm font-bold text-stone-800 dark:text-stone-100">{desktopActive?.label}</p>
            </div>
            <button onClick={() => setActiveKey(null)} className="text-stone-500 hover:text-stone-700"><X size={18} /></button>
          </div>
          <div className="divide-y divide-stone-100 dark:divide-stone-800 max-h-[60vh] overflow-y-auto">
            {desktopActive?.sheet.map(item => {
              const Icon = item.icon;
              const isCurrent = location.pathname + location.search === item.path
                             || (!item.path.includes('?') && location.pathname === item.path);
              const tone    = item.tone || 'default';
              const iconBox = isCurrent
                ? (tone === 'emerald' ? 'bg-emerald-600 text-white ring-2 ring-emerald-300/60' : 'bg-[#6B4D5F] text-white')
                : (tone === 'emerald' ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 ring-1 ring-emerald-200 dark:ring-emerald-800/60' : 'bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-300');
              return (
                <button key={item.path}
                  data-testid={`dock-sheet-item-${item.path.replace(/[/?=&]/g,'-').replace(/^-+/,'')}`}
                  onClick={() => navTo(item.path)}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-stone-50 dark:hover:bg-stone-800/50 text-left transition">
                  <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${iconBox}`}>
                    <Icon size={18} weight={isCurrent ? 'fill' : 'duotone'} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-semibold truncate ${tone === 'emerald' ? 'text-emerald-800 dark:text-emerald-200' : 'text-stone-800 dark:text-stone-100'}`}>
                      {item.label}
                      {tone === 'emerald' && <span className="ml-1.5 inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse align-middle" />}
                    </p>
                    <p className="text-[11px] text-stone-500 truncate">{item.desc}</p>
                  </div>
                  <CaretRight size={14} className={tone === 'emerald' ? 'text-emerald-400' : 'text-stone-400'} />
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── DESKTOP STATIC DOCK (lg+) ────────────────────────────────────── */}
      <div
        className={`fixed bottom-safe left-1/2 -translate-x-1/2 z-30 transition-transform duration-300 ease-out hidden lg:block
          ${hidden ? 'translate-y-[140%]' : 'translate-y-0'}`}
        style={{ minWidth: 560 }}
      >
        <div className="absolute -inset-[2px] rounded-[22px] bg-gradient-to-r from-[#6B4D5F]/30 via-[#9B7D8F]/20 to-[#6B4D5F]/30 blur-md opacity-0 pointer-events-none" />
        <div className="relative rounded-[20px] bg-gradient-to-b from-white/95 to-white/80 dark:from-stone-900/95 dark:to-stone-950/85 backdrop-blur-2xl ring-1 ring-stone-200/70 dark:ring-white/10 shadow-[0_12px_40px_-8px_rgba(60,30,40,0.35)] px-2 py-1.5">
          <div className="flex items-stretch justify-around gap-0.5">
            {items.map(d => {
              const Icon = d.icon;
              const active   = isActive(d);
              const opened   = activeKey === d.key;
              const highlighted = opened || active;
              return (
                <button key={d.key} onClick={() => onDesktopTap(d)}
                  data-testid={`dock-${d.key}`}
                  className={`relative flex-1 flex flex-col items-center gap-0.5 py-2 px-1 rounded-2xl transition-all duration-200 group
                    ${highlighted
                      ? 'bg-gradient-to-br from-[#6B4D5F] to-[#3D2B33] text-white shadow-[0_6px_18px_-4px_rgba(107,77,95,0.55)] scale-[1.04]'
                      : 'text-stone-500 dark:text-stone-400 hover:text-[#6B4D5F] dark:hover:text-amber-300 active:scale-95'}`}>
                  {highlighted && <span className="absolute inset-x-3 top-0 h-px bg-gradient-to-r from-transparent via-white/70 to-transparent rounded-full" />}
                  <Icon size={20} weight={highlighted ? 'fill' : 'duotone'} className="transition-transform duration-200 group-active:scale-90" />
                  <span className={`text-[10px] font-semibold tracking-tight transition-opacity ${highlighted ? 'opacity-100' : 'opacity-80'}`}>
                    {d.label}
                  </span>
                  {active && !opened && <span className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 h-1 w-1 rounded-full bg-[#6B4D5F] dark:bg-amber-400" />}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── MOBILE CONVEYOR BUBBLE (< lg) ──────────────────────────────────── */}
      {showBubble && (
        <div
          onClick={() => setBubbleOpen(false)}
          className="fixed inset-0 z-40 lg:hidden"
        />
      )}
      <div
        className="lg:hidden"
        style={{
          position: 'fixed',
          bottom: `calc(88px + env(safe-area-inset-bottom, 0px))`,
          left: '50%',
          transform: `translateX(-50%) translateY(${showBubble ? '0' : '16px'})`,
          opacity: showBubble ? 1 : 0,
          pointerEvents: showBubble ? 'auto' : 'none',
          transition: 'opacity 220ms ease, transform 280ms cubic-bezier(.34,1.56,.64,1)',
          zIndex: 41,
          width: 'calc(100vw - 32px)',
          maxWidth: 420,
        }}
      >
        <div style={{
          borderRadius: 20, overflow: 'hidden',
          background: 'rgba(255,255,255,0.9)',
          backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)',
          boxShadow: `0 8px 40px -4px ${accentCfg.shadow}, 0 2px 12px rgba(0,0,0,0.12)`,
          border: '1px solid rgba(255,255,255,0.6)',
        }} className="dark:!bg-stone-900/92 dark:!border-white/10">
          {/* Bubble header */}
          <div style={{ padding: '10px 14px 8px', borderBottom: '1px solid rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{
                width: 28, height: 28, borderRadius: 9, flexShrink: 0,
                background: `linear-gradient(135deg, ${accentCfg.from}, ${accentCfg.to})`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: `0 4px 10px ${accentCfg.shadow}`,
              }}>
                {centered && <centered.icon size={14} weight="fill" color="white" />}
              </div>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#1c1c1e' }} className="dark:!text-stone-100">{centered?.label}</span>
            </div>
            <button onClick={() => setBubbleOpen(false)} style={{ color: '#8e8e93', padding: 2 }} data-testid="bubble-close">
              <X size={15} />
            </button>
          </div>
          {/* Bubble rows */}
          <div>
            {centered?.sheet.map((item, i) => {
              const Icon = item.icon;
              const current = location.pathname + location.search === item.path
                || (!item.path.includes('?') && location.pathname === item.path);
              const isEmerald = item.tone === 'emerald';
              return (
                <button key={item.path}
                  data-testid={`dock-sheet-item-${item.path.replace(/[/?=&]/g,'-').replace(/^-+/,'')}`}
                  onClick={() => navTo(item.path)}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                    padding: '9px 14px', background: 'transparent', cursor: 'pointer',
                    borderBottom: i < centered.sheet.length - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none',
                  }}
                  className="hover:bg-stone-50 dark:hover:bg-stone-800/50">
                  <div style={{
                    width: 36, height: 36, borderRadius: 11, flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: current
                      ? `linear-gradient(135deg, ${accentCfg.from}, ${accentCfg.to})`
                      : isEmerald ? 'rgba(16,185,129,0.12)' : 'rgba(0,0,0,0.06)',
                    boxShadow: current ? `0 4px 12px ${accentCfg.shadow}` : 'none',
                  }}>
                    <Icon size={16} weight={current ? 'fill' : 'duotone'}
                      color={current ? '#fff' : isEmerald ? '#10B981' : '#636366'} />
                  </div>
                  <div style={{ flex: 1, textAlign: 'left', minWidth: 0 }}>
                    <p style={{ fontSize: 13, fontWeight: 600, margin: 0, color: current ? accentCfg.from : '#1c1c1e', display: 'flex', alignItems: 'center', gap: 6 }}
                      className="dark:!text-stone-100">
                      {item.label}
                      {isEmerald && <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#10B981', display: 'inline-block' }} />}
                    </p>
                    <p style={{ fontSize: 11, margin: 0, color: '#8e8e93', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.desc}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── MOBILE CONVEYOR DOCK (< lg) ──────────────────────────────────── */}
      <div
        data-testid="bottom-dock"
        className="lg:hidden"
        style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 30,
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          transform: hidden ? 'translateY(110%)' : 'translateY(0)',
          transition: 'transform 300ms cubic-bezier(.4,0,.2,1)',
        }}
      >
        <div style={{
          margin: '0 12px 8px', borderRadius: 22,
          // Translucent frosted-glass bar
          background: 'rgba(255, 255, 255, 0.72)',
          backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
          boxShadow: '0 -1px 0 rgba(255,255,255,0.6), 0 8px 32px rgba(0,0,0,0.10)',
          border: '1px solid rgba(255,255,255,0.55)',
          // overflow: visible so the 1.4× center item pops above the bar edge
          overflow: 'visible', position: 'relative',
          // Extra top padding creates the "shelf" the center icon pops out of
          paddingTop: 12,
        }} className="dark:!bg-stone-900/75 dark:!border-white/10">
          {/* Accent glow line at top */}
          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0, height: 1,
            background: `linear-gradient(90deg, transparent, ${accentCfg.from}66, ${accentCfg.to}66, transparent)`,
            transition: 'background 300ms ease',
          }} />
          {/* Centre focus zone */}
          <div style={{
            position: 'absolute', top: 0, bottom: 0,
            left: '50%', width: SLOT, transform: 'translateX(-50%)',
            pointerEvents: 'none', borderRadius: 14,
            background: `linear-gradient(180deg, ${accentCfg.from}18, transparent)`,
            transition: 'background 300ms ease',
          }} />
          {/* Scrollable track */}
          <div
            ref={trackRef}
            onScroll={onConveyorScroll}
            style={{
              display: 'flex', overflowX: 'scroll', overflowY: 'hidden',
              scrollSnapType: 'x mandatory', scrollbarWidth: 'none',
              msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch',
              paddingTop: 4, paddingBottom: 10,
              paddingLeft:  0,
              paddingRight: 'calc(50% - 36px)',
            }}
          >
            {loopItems.map((d, extI) => {
              const acc     = ACCENT[d.key] || ACCENT.po;
              const focused = extI === centeredIdx;
              const active  = isActive(d);
              return (
                <button
                  key={`${d.key}-${extI}`}
                  data-testid={extI >= N && extI < N * 2 ? `dock-${d.key}` : undefined}
                  onClick={() => {
                    if (focused) {
                      if (d.sheet.length === 1) { navTo(d.sheet[0].path); return; }
                      setBubbleOpen(b => !b);
                    } else {
                      setBubbleOpen(false);
                      const el = trackRef.current;
                      if (el) {
                        const dest = Math.max(0, extI * SLOT + SLOT / 2 - el.offsetWidth / 2);
                        el.scrollTo({ left: dest, behavior: 'smooth' });
                      }
                    }
                  }}
                  style={{
                    flexShrink: 0, width: SLOT, scrollSnapAlign: 'center',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                    border: 'none', background: 'transparent', cursor: 'pointer', padding: '4px 0',
                    opacity: getOpacity(extI),
                    transform: `scale(${getScale(extI)})`,
                    transition: 'transform 220ms cubic-bezier(.4,0,.2,1), opacity 220ms ease',
                    transformOrigin: 'center bottom',
                  }}
                >
                  <div style={{
                    width: 40, height: 40, borderRadius: 14,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: focused
                      ? `linear-gradient(145deg, ${acc.from}, ${acc.to})`
                      : active ? `${acc.from}22` : 'rgba(0,0,0,0.05)',
                    boxShadow: focused ? `0 6px 18px ${acc.shadow}, 0 1px 3px rgba(0,0,0,0.15)` : 'none',
                    transition: 'background 250ms ease, box-shadow 250ms ease',
                    position: 'relative', overflow: 'hidden',
                  }} className={!focused && !active ? 'dark:!bg-white/8' : ''}>
                    {focused && (
                      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '50%', background: 'linear-gradient(180deg,rgba(255,255,255,.25),transparent)', borderRadius: '14px 14px 0 0' }} />
                    )}
                  <d.icon size={focused ? 22 : 20} weight={focused || active ? 'fill' : 'duotone'}
                      color={focused ? '#fff' : active ? acc.from : '#636366'} />
                  </div>
                  <span style={{
                    fontSize: 10, fontWeight: focused ? 700 : 500,
                    color: focused ? acc.from : active ? acc.from : '#636366',
                    letterSpacing: focused ? '0.01em' : '0.02em',
                    transition: 'all 200ms ease', whiteSpace: 'nowrap',
                  }} className="dark:!text-stone-400">{d.label}</span>
                  {active && !focused && (
                    <div style={{ width: 4, height: 4, borderRadius: '50%', background: acc.from, marginTop: -2 }} />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Reveal hint (mobile only) */}
      {hidden && (
        <button onClick={() => setHidden(false)} data-testid="dock-reveal-hint"
          aria-label="Show navigation"
          className="lg:hidden fixed bottom-[calc(env(safe-area-inset-bottom,0px)+8px)] left-1/2 -translate-x-1/2 z-30 px-3 py-0.5 rounded-full bg-stone-900/70 text-white text-[10px]">
          Show menu
        </button>
      )}
    </>
  );
}
